import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import axios from 'axios';
import { useAccount } from 'wagmi';
import logger from '../utils/logger';
import {
  buildActiveTrades,
  buildClaimableByMarket,
  buildClaimableList,
  buildPositionsByMarket,
} from '../utils/walletPortfolioUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://3dragons.io';

const POLL_MS = parseInt(process.env.REACT_APP_WALLET_PORTFOLIO_POLL_MS || '', 10) || 60_000;

const WalletPortfolioContext = createContext(null);

export function WalletPortfolioProvider({ children }) {
  const { address, isConnected } = useAccount();
  const [claimableMarkets, setClaimableMarkets] = useState([]);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const refreshPortfolio = useCallback(async () => {
    if (!isConnected || !address) {
      setClaimableMarkets([]);
      setTrades([]);
      setLoading(false);
      setHasLoadedOnce(true);
      return;
    }

    try {
      setLoading(true);
      const [claimableResponse, tradesResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/claimable/${address}`),
        axios.get(
          `${BACKEND_URL}/api/trade-history/${address}?limit=50&status=all&skipCount=1`
        ),
      ]);

      if (claimableResponse.data?.success) {
        setClaimableMarkets(claimableResponse.data.claimableMarkets || []);
      }

      if (tradesResponse.data?.success) {
        setTrades(tradesResponse.data.trades || []);
      }
    } catch (error) {
      logger.warn('Wallet portfolio refresh failed:', error?.message || error);
    } finally {
      setLoading(false);
      setHasLoadedOnce(true);
    }
  }, [address, isConnected]);

  useEffect(() => {
    refreshPortfolio();

    const interval = setInterval(refreshPortfolio, POLL_MS);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        refreshPortfolio();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refreshPortfolio]);

  const value = useMemo(
    () => ({
      loading,
      hasLoadedOnce,
      refreshPortfolio,
      claimableList: buildClaimableList(claimableMarkets),
      claimableByMarket: buildClaimableByMarket(claimableMarkets),
      userMarketPositions: buildPositionsByMarket(trades),
      activeTrades: buildActiveTrades(trades),
    }),
    [claimableMarkets, trades, loading, hasLoadedOnce, refreshPortfolio]
  );

  return (
    <WalletPortfolioContext.Provider value={value}>{children}</WalletPortfolioContext.Provider>
  );
}

export function useWalletPortfolio() {
  const ctx = useContext(WalletPortfolioContext);
  if (!ctx) {
    throw new Error('useWalletPortfolio must be used within WalletPortfolioProvider');
  }
  return ctx;
}
